import { MAX_CAMADAS_POR_CELULA, } from "./camadas.js";
import { linhasDoTexto } from "./fontes-de-texto.js";
const paraRad = (graus) => (graus * Math.PI) / 180;
const paraGraus = (rad) => (rad * 180) / Math.PI;
const limitar = (v, min, max) => Math.min(Math.max(v, min), max);
/** Duas casas bastam (um centésimo de ponto percentual: 0,1 px na folha) e tiram o ruído das contas. */
const duasCasas = (n) => Math.round(n * 100) / 100;
// ─── Girar, caixa, cantos ─────────────────────────────────────────────────────
/** Gira `ponto` em torno de `centro`, `graus` no sentido horário (com o `y` para baixo, como na tela). */
export function girarPonto(ponto, centro, graus) {
    const r = paraRad(graus);
    const cos = Math.cos(r);
    const sen = Math.sin(r);
    const dx = ponto.x - centro.x;
    const dy = ponto.y - centro.y;
    return { x: centro.x + dx * cos - dy * sen, y: centro.y + dx * sen + dy * cos };
}
/** O centro de uma camada em pixels da célula. */
export function centroDaCamada(camada, celula) {
    return { x: (camada.x / 100) * celula.largura, y: (camada.y / 100) * celula.altura };
}
/** A caixa de uma camada cujo conteúdo mede `tamanho` px (sem girar), centrada onde a camada manda. */
export function caixaDaCamada(camada, tamanho, celula) {
    const c = centroDaCamada(camada, celula);
    return {
        cx: c.x,
        cy: c.y,
        largura: tamanho.largura,
        altura: tamanho.altura,
        rotacao: camada.rotacao,
    };
}
/**
 * O tamanho que uma camada de imagem tem na célula, em pixels: a largura é `largura`% da célula e a
 * altura sai da proporção da imagem (nada é esticado).
 */
export function tamanhoDaCamadaDeImagem(camada, imagem, celula) {
    const largura = (camada.largura / 100) * celula.largura;
    return { largura, altura: (largura * imagem.altura) / imagem.largura };
}
/** A caixa de uma camada de imagem em pixels da célula, dado o tamanho natural da imagem. */
export function caixaDaCamadaDeImagem(camada, imagem, celula) {
    return caixaDaCamada(camada, tamanhoDaCamadaDeImagem(camada, imagem, celula), celula);
}
/**
 * A caixa de uma camada de texto: o bloco mede `bloco` px (a maior linha por `linhas × corpo × entrelinha`
 * de altura) e é ancorado pelo **centro**. Quem mede o bloco é o servidor (`desenharTextoDaCamada`) ou,
 * na prévia, o elemento na tela.
 */
export function caixaDaCamadaDeTexto(camada, bloco, celula) {
    return caixaDaCamada(camada, bloco, celula);
}
/** O tamanho da letra em pixels: o corpo da camada de texto é % da largura da célula. */
export const corpoEmPx = (camada, celula) => (camada.corpo / 100) * celula.largura;
/**
 * A altura do bloco de texto em pixels da célula: `linhas × corpo × entrelinha`. Não depende da fonte, então
 * a tela a calcula sozinha; a **largura** do bloco (a da linha mais larga) depende dos avanços da fonte, e a
 * tela a mede no navegador, com a mesma fonte carregada de `/api/fontes`, ou lê do servidor
 * (`desenharTextoDaCamada` devolve `bloco`).
 */
export function alturaDoBlocoDeTexto(camada, celula) {
    return linhasDoTexto(camada.texto).length * corpoEmPx(camada, celula) * camada.entrelinha;
}
/**
 * Os quatro cantos da caixa **já girados**, na ordem: superior esquerdo, superior direito, inferior
 * direito, inferior esquerdo (contados antes de girar). É o contorno da caixa de seleção e das alças.
 */
export function cantosDaCaixa(caixa) {
    const c = { x: caixa.cx, y: caixa.cy };
    const mw = caixa.largura / 2;
    const mh = caixa.altura / 2;
    const gira = (dx, dy) => girarPonto({ x: c.x + dx, y: c.y + dy }, c, caixa.rotacao);
    return [gira(-mw, -mh), gira(mw, -mh), gira(mw, mh), gira(-mw, mh)];
}
/** O menor retângulo reto que contém a caixa girada (para saber se ela toca a célula). */
export function caixaEnvolvente(caixa) {
    const cantos = cantosDaCaixa(caixa);
    const xs = cantos.map((p) => p.x);
    const ys = cantos.map((p) => p.y);
    const x = Math.min(...xs);
    const y = Math.min(...ys);
    return { x, y, largura: Math.max(...xs) - x, altura: Math.max(...ys) - y };
}
/**
 * A caixa toca a célula? Por contenção do retângulo reto que envolve a caixa girada: pode dizer que sim
 * para uma camada girada cujos cantos vazios é que tocam a célula, e isso é inofensivo (o servidor só
 * desenharia nada). Nunca diz que não para uma que toca.
 */
export function caixaTocaACelula(caixa, celula) {
    const e = caixaEnvolvente(caixa);
    return e.x < celula.largura && e.y < celula.altura && e.x + e.largura > 0 && e.y + e.altura > 0;
}
// ─── Acertar o clique ─────────────────────────────────────────────────────────
/**
 * O ponto está dentro da camada girada? Leva o ponto para o referencial da caixa (gira ao contrário em
 * torno do centro) e confere o retângulo reto. `folga` (px) alarga a área, para o dedo no celular.
 */
export function pontoDentroDaCaixa(ponto, caixa, folga = 0) {
    const local = girarPonto(ponto, { x: caixa.cx, y: caixa.cy }, -caixa.rotacao);
    return (Math.abs(local.x - caixa.cx) <= caixa.largura / 2 + folga &&
        Math.abs(local.y - caixa.cy) <= caixa.altura / 2 + folga);
}
/**
 * A camada de cima que o clique acerta: percorre da última (a de cima) para a primeira. `null` quando o
 * clique cai em nenhuma — a tela então tira a seleção.
 */
export function camadaNoPonto(itens, ponto, folga = 0) {
    for (let i = itens.length - 1; i >= 0; i--) {
        const item = itens[i];
        if (item && pontoDentroDaCaixa(ponto, item.caixa, folga))
            return item.id;
    }
    return null;
}
// ─── Limites ──────────────────────────────────────────────────────────────────
/** Graus na faixa (-180, 180]: 270° é -90°, e -180° é 180°. */
export function normalizarGraus(graus) {
    const n = ((((graus + 180) % 360) + 360) % 360) - 180;
    return n === -180 ? 180 : n;
}
const LARGURA_MIN = 2;
const LARGURA_MAX = 200;
const CORPO_MIN = 1;
const CORPO_MAX = 60;
/**
 * Põe a camada dentro do que o servidor aceita (os limites de `camadas.ts`) e **dentro da célula**: o
 * centro nunca sai dela. Uma camada pode passar da borda — metade para fora é um uso legítimo, e o
 * servidor a corta —, mas com o centro fora não há o que agarrar na tela nem o que ver no papel. A
 * rotação vai para (-180, 180] e os números, a duas casas.
 */
export function limitarCamada(camada) {
    const base = {
        x: duasCasas(limitar(camada.x, 0, 100)),
        y: duasCasas(limitar(camada.y, 0, 100)),
        rotacao: duasCasas(normalizarGraus(camada.rotacao)),
    };
    if (camada.tipo === "imagem") {
        return {
            ...camada,
            ...base,
            largura: duasCasas(limitar(camada.largura, LARGURA_MIN, LARGURA_MAX)),
        };
    }
    return { ...camada, ...base, corpo: duasCasas(limitar(camada.corpo, CORPO_MIN, CORPO_MAX)) };
}
// ─── Gestos ───────────────────────────────────────────────────────────────────
/** Arrasta a camada `delta` pixels da célula. */
export function moverCamada(camada, delta, celula) {
    return limitarCamada({
        ...camada,
        x: camada.x + (delta.x / celula.largura) * 100,
        y: camada.y + (delta.y / celula.altura) * 100,
    });
}
const SINAL_DA_ALCA = {
    nw: { x: -1, y: -1 },
    ne: { x: 1, y: -1 },
    se: { x: 1, y: 1 },
    sw: { x: -1, y: 1 },
};
/** A escala que leva a camada ao limite: `largura` de 2 a 200 %, `corpo` de 1 a 60 %. */
function limitesDaEscala(camada) {
    return camada.tipo === "imagem"
        ? { min: LARGURA_MIN / camada.largura, max: LARGURA_MAX / camada.largura }
        : { min: CORPO_MIN / camada.corpo, max: CORPO_MAX / camada.corpo };
}
/** Multiplica o tamanho da camada: a `largura` da imagem, o `corpo` do texto. */
function escalada(camada, fator) {
    return camada.tipo === "imagem"
        ? { ...camada, largura: camada.largura * fator }
        : { ...camada, corpo: camada.corpo * fator };
}
/**
 * Estica a camada puxando uma alça de canto até o `ponteiro` (pixels da célula), sem mudar a proporção.
 * `camada` e `caixa` são os do **início** do gesto — a conta parte sempre deles, e não do que o gesto
 * já fez, para não acumular erro.
 *
 * Por padrão o **canto oposto fica parado** (o centro anda); com `aPartirDoCentro` o centro fica parado e
 * a camada cresce para os dois lados. Em ambos o fator sai da projeção do ponteiro na diagonal da caixa:
 * puxar na direção da diagonal estica, e puxar para o lado oposto encolhe até o mínimo.
 */
export function redimensionarPorAlca(camada, caixa, alca, ponteiro, celula, opcoes = {}) {
    const centro = { x: caixa.cx, y: caixa.cy };
    const sinal = SINAL_DA_ALCA[alca];
    // Do centro até a alça, no referencial da caixa (sem girar).
    const meia = { x: (sinal.x * caixa.largura) / 2, y: (sinal.y * caixa.altura) / 2 };
    const { min, max } = limitesDaEscala(camada);
    let fator;
    let novoCentro;
    if (opcoes.aPartirDoCentro) {
        const v = girarPonto(ponteiro, centro, -caixa.rotacao);
        fator = ((v.x - centro.x) * meia.x + (v.y - centro.y) * meia.y) / (meia.x ** 2 + meia.y ** 2);
        fator = limitar(fator, min, max);
        novoCentro = centro;
    }
    else {
        // O canto oposto, já girado, é a âncora; o vetor da âncora à alça é a diagonal inteira.
        const ancora = girarPonto({ x: centro.x - meia.x, y: centro.y - meia.y }, centro, caixa.rotacao);
        const v = girarPonto(ponteiro, ancora, -caixa.rotacao);
        const diagonal = { x: 2 * meia.x, y: 2 * meia.y };
        fator =
            ((v.x - ancora.x) * diagonal.x + (v.y - ancora.y) * diagonal.y) /
                (diagonal.x ** 2 + diagonal.y ** 2);
        fator = limitar(fator, min, max);
        novoCentro = girarPonto({ x: ancora.x + (fator * diagonal.x) / 2, y: ancora.y + (fator * diagonal.y) / 2 }, ancora, caixa.rotacao);
    }
    return limitarCamada({
        ...escalada(camada, fator),
        x: (novoCentro.x / celula.largura) * 100,
        y: (novoCentro.y / celula.altura) * 100,
    });
}
/** O ângulo, em graus (horário, `y` para baixo), do vetor de `de` até `para`. */
const anguloEntre = (de, para) => paraGraus(Math.atan2(para.y - de.y, para.x - de.x));
/**
 * Encaixa o ângulo. Com `passo` (a tecla Shift): no múltiplo mais próximo (15° por padrão). Sem ele, um
 * ímã fraco: dentro de `ima` graus de 0, 90, 180 ou -90, vai para eles — o operador acerta "reto" sem
 * mirar, e passa direto por eles se girar de propósito.
 */
export function encaixarAngulo(graus, opcoes = {}) {
    const { passo, ima = 3 } = opcoes;
    if (passo !== undefined && passo > 0)
        return normalizarGraus(Math.round(graus / passo) * passo);
    const reto = Math.round(graus / 90) * 90;
    return Math.abs(graus - reto) <= ima ? normalizarGraus(reto) : graus;
}
/**
 * Gira a camada em torno do `centro` (pixels da célula), do ponto em que o ponteiro **começou**
 * (`inicio`) até onde ele está (`atual`): a camada gira o ângulo que o ponteiro girou, seja qual for a
 * alça pegada. `camada` é a do início do gesto. `encaixe` (Shift): de 15° em 15°; sem ele, o ímã de
 * 0/90°.
 */
export function girarPorPonteiro(camada, centro, inicio, atual, opcoes = {}) {
    // O ponteiro em cima do centro não tem ângulo: a camada fica como está.
    if (Math.hypot(atual.x - centro.x, atual.y - centro.y) < 1e-6)
        return camada;
    const graus = camada.rotacao + (anguloEntre(centro, atual) - anguloEntre(centro, inicio));
    const normal = normalizarGraus(graus);
    const rotacao = opcoes.encaixe ? encaixarAngulo(normal, { passo: 15 }) : encaixarAngulo(normal);
    return limitarCamada({ ...camada, rotacao });
}
/**
 * A pinça de dois dedos: a camada **acompanha** os dedos. O que estava sob os dedos continua sob eles —
 * a escala é a razão das distâncias, a rotação é a diferença dos ângulos, e o centro vai com o ponto
 * médio. `camada` é a do início do gesto, `inicio` e `atual` os dois dedos nos dois momentos.
 */
export function transformarPorPinca(camada, inicio, atual, celula) {
    const d0 = Math.hypot(inicio[1].x - inicio[0].x, inicio[1].y - inicio[0].y);
    const d1 = Math.hypot(atual[1].x - atual[0].x, atual[1].y - atual[0].y);
    if (d0 < 1e-6)
        return camada;
    const { min, max } = limitesDaEscala(camada);
    const fator = limitar(d1 / d0, min, max);
    const delta = anguloEntre(atual[0], atual[1]) - anguloEntre(inicio[0], inicio[1]);
    const m0 = { x: (inicio[0].x + inicio[1].x) / 2, y: (inicio[0].y + inicio[1].y) / 2 };
    const m1 = { x: (atual[0].x + atual[1].x) / 2, y: (atual[0].y + atual[1].y) / 2 };
    const c0 = centroDaCamada(camada, celula);
    // O centro gira em torno do ponto médio inicial, escala, e vai para o ponto médio atual.
    const girado = girarPonto(c0, m0, delta);
    const novo = {
        x: m1.x + (girado.x - m0.x) * fator,
        y: m1.y + (girado.y - m0.y) * fator,
    };
    return limitarCamada({
        ...escalada(camada, fator),
        x: (novo.x / celula.largura) * 100,
        y: (novo.y / celula.altura) * 100,
        rotacao: normalizarGraus(camada.rotacao + delta),
    });
}
// ─── A pilha de camadas ───────────────────────────────────────────────────────
/** O primeiro `c1`, `c2`… que nenhuma camada usa. Determinístico: nada de relógio nem de sorteio. */
export function novoIdDeCamada(usados) {
    const ocupados = new Set(usados);
    let n = 1;
    while (ocupados.has(`c${n}`))
        n++;
    return `c${n}`;
}
/** A distância, em % da célula, entre uma camada e a sua cópia: o bastante para ver que são duas. */
export const DESLOCAMENTO_DA_COPIA = 4;
/**
 * Duplica a camada `id`: a cópia vem logo acima dela, deslocada um pouco para baixo e para a direita
 * (e dentro da célula), com um `id` novo. `null` se a camada não existe ou a célula já está no limite.
 */
export function duplicarCamada(camadas, id, deslocamento = DESLOCAMENTO_DA_COPIA) {
    const i = camadas.findIndex((c) => c.id === id);
    const original = camadas[i];
    if (!original || camadas.length >= MAX_CAMADAS_POR_CELULA)
        return null;
    const novaId = novoIdDeCamada(camadas.map((c) => c.id));
    const copia = limitarCamada({
        ...original,
        id: novaId,
        x: original.x + deslocamento,
        y: original.y + deslocamento,
    });
    return { camadas: [...camadas.slice(0, i + 1), copia, ...camadas.slice(i + 1)], novaId };
}
/**
 * Muda a camada `id` de lugar na pilha. A ordem do array é de **baixo para cima**: o fim é a frente.
 * `frente` e `tras` vão até a ponta; `uma-acima` e `uma-abaixo` trocam com a vizinha. Devolve um array
 * novo (o mesmo, sem copiar, se não há o que mudar).
 */
export function reordenarCamada(camadas, id, para) {
    const i = camadas.findIndex((c) => c.id === id);
    if (i < 0)
        return [...camadas];
    const j = para === "frente"
        ? camadas.length - 1
        : para === "tras"
            ? 0
            : limitar(i + (para === "uma-acima" ? 1 : -1), 0, camadas.length - 1);
    if (i === j)
        return [...camadas];
    const saida = [...camadas];
    const [camada] = saida.splice(i, 1);
    if (camada)
        saida.splice(j, 0, camada);
    return saida;
}
/**
 * "Aplicar em todas as fotos": copia as camadas da célula `origem` para as outras. Células `null` (sem
 * foto) ficam como estão, e a de origem não muda.
 *
 * `substituir` (o padrão) deixa as outras com **as mesmas camadas** da origem. `acrescentar` põe as da
 * origem por cima das que a outra já tem, com `id`s novos onde colidem; a célula que passaria do limite
 * (`MAX_CAMADAS_POR_CELULA`) fica como estava e entra em `naoCouberam`, para a tela avisar.
 */
export function aplicarEmTodas(celulas, origem, modo = "substituir") {
    const daOrigem = celulas[origem] ?? [];
    const naoCouberam = [];
    const saida = celulas.map((existentes, i) => {
        if (existentes === null)
            return null;
        if (i === origem)
            return [...existentes];
        const copias = daOrigem.map((c) => ({ ...c }));
        if (modo === "substituir")
            return copias;
        if (existentes.length + copias.length > MAX_CAMADAS_POR_CELULA) {
            naoCouberam.push(i);
            return [...existentes];
        }
        const usados = existentes.map((c) => c.id);
        const novas = copias.map((c) => {
            if (!usados.includes(c.id)) {
                usados.push(c.id);
                return c;
            }
            const id = novoIdDeCamada(usados);
            usados.push(id);
            return { ...c, id };
        });
        return [...existentes, ...novas];
    });
    return { celulas: saida, naoCouberam };
}
/**
 * A camada de imagem escrita para CSS (`position: absolute` dentro da célula com `overflow: hidden`), na
 * mesma conta de `caixaDaCamadaDeImagem`. Só a proporção da célula entra, não a unidade. A camada de
 * texto não precisa disto: `left: x%`, `top: y%`, `transform: translate(-50%, -50%) rotate(<rotacao>deg)`
 * e `font-size` de `corpoEmPx`.
 */
export function estiloCssDaCamadaDeImagem(camada, imagem, celula) {
    const tamanho = tamanhoDaCamadaDeImagem(camada, imagem, celula);
    const largura = (tamanho.largura / celula.largura) * 100;
    const altura = (tamanho.altura / celula.altura) * 100;
    return {
        left: camada.x - largura / 2,
        top: camada.y - altura / 2,
        width: largura,
        height: altura,
        transform: `rotate(${camada.rotacao}deg)`,
    };
}
//# sourceMappingURL=camadas-geometria.js.map